import * as React from "react";
import { useState } from "react";
import {
    Button,
    Checkbox,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Tooltip,
    makeStyles,
    tokens,
} from "@fluentui/react-components";
import {
    InfoRegular,
    EditRegular,
    EyeRegular,
    LightbulbRegular,
    LightbulbFilled,
    CopyRegular,
    DismissRegular,
    ArrowSyncRegular,
    SearchRegular

} from "@fluentui/react-icons";
import { InvalidOccurrence } from "../taskpane";
import { ValidStandard } from "../../api/IsoApiServer";

// Assuming these types are defined elsewhere, e.g., in api/IsoApiServer




interface InvalidOccurrenceItemProps {
    occurrence: InvalidOccurrence;
    index: number;
    selected: boolean;
    onClick: (occurrence: InvalidOccurrence) => void;
    onReplaceChange: (occurrence: InvalidOccurrence, replaceAll: boolean, replacementText?: string) => void;
}

const useStyles = makeStyles({
    item: {
        padding: "8px",
        cursor: "pointer",
        backgroundColor: tokens.colorNeutralBackground2,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
        width: "95%",
        maxWidth: '95%',
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        "&:hover": {
            backgroundColor: tokens.colorNeutralBackground2Hover,
        },
    },
    selected: {
        backgroundColor: tokens.colorBrandBackground2,
    },
    content: {
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },


    actions: {
        display: "flex",
        gap: "4px",
    },
    popoverContent: {
        padding: "16px",
        minWidth: "200px",
        maxHeight: "75vh",
        overflow: "auto",
    },
    list: {
        listStyle: "none",
        padding: 0,
        margin: 0,
    },
    listItem: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "4px 0",
    },

    listItemContent: {
        flexGrow: 1,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        minWidth: 0,
        wordWrap: "break-word", // Ensures text wraps within its container
        wordBreak: "break-word", // Breaks long words if necessary
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground2,
        wordWrap: "break-word", // Ensures text wraps within its container
        wordBreak: "break-word", // Breaks long words if necessary
    },
    subtitle: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        wordWrap: "break-word", // Ensures text wraps within its container
        wordBreak: "break-word", // Breaks long words if necessary
    },
    listItemActions: {
        display: "flex",
        gap: "4px",
    },
});

const InvalidOccurrenceItem: React.FC<InvalidOccurrenceItemProps> = ({
    occurrence,
    selected,
    onClick,
    onReplaceChange,
}) => {
    const styles = useStyles();
    const [checked, setChecked] = useState<boolean | 'mixed'>(false);

    const handleReplaceChange = (standard: ValidStandard) => {
        const updatedOccurrence = { ...occurrence };
        if (updatedOccurrence.validStandards) {
            updatedOccurrence.validStandards = updatedOccurrence.validStandards.map((vs) =>
                vs.id === standard.id ? { ...vs, replace: standard.replace } as ValidStandard : { ...vs, replace: false } as ValidStandard
            );
        }
        onReplaceChange(updatedOccurrence, false, standard.identificator);
    };


    return (
        <div
            className={`${styles.item} ${selected ? styles.selected : ""}`}
            onClick={() => onClick(occurrence)}
        >
            <Checkbox checked={checked} onChange={(_, data) => setChecked(data.checked)} size="medium" />
            <div className={styles.content}>
                <span className={styles.title}>
                    {occurrence.label}
                </span>
                <span className={styles.subtitle}>
                    {'Platné do: '}{occurrence.validToString}
                </span>
            </div>
            <div className={styles.actions}>
                {occurrence.anotation && (
                    <Popover trapFocus>
                        <PopoverTrigger disableButtonEnhancement>
                            <Tooltip content="Zobrazit poznámku" relationship="label">
                                <Button
                                    icon={<EditRegular />}
                                    size="small"
                                    appearance="subtle"
                                />
                            </Tooltip>
                        </PopoverTrigger>
                        <PopoverSurface className={styles.popoverContent}>
                            <div>
                                <strong>Poznámka:</strong>
                                <p>{occurrence.anotation}</p>
                            </div>
                        </PopoverSurface>
                    </Popover>
                )}
                <Popover trapFocus>
                    <PopoverTrigger disableButtonEnhancement>
                        <Tooltip content="Zobrazit popis" relationship="label">
                            <Button
                                icon={<InfoRegular />}
                                size="small"
                                appearance="subtle"
                            />
                        </Tooltip>
                    </PopoverTrigger>
                    <PopoverSurface className={styles.popoverContent}>
                        <div>
                            <strong>Popis:</strong>
                            <p>{occurrence.description ?? "Žádný popis"}</p>
                        </div>
                    </PopoverSurface>
                </Popover>
                {occurrence.detailUrl && (
                    <Tooltip content="Otevřít náhled/standard" relationship="label">
                        <Button
                            icon={<EyeRegular />}
                            size="small"
                            appearance="subtle"
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open(occurrence.detailUrl, "_blank");
                            }}
                        />
                    </Tooltip>
                )}
                <Popover trapFocus>
                    <PopoverTrigger disableButtonEnhancement>
                        <Tooltip content="Návazné standardy" relationship="label">
                            <Button
                                icon={
                                    occurrence.validStandards?.some((p) => p.replace) ? (
                                        <LightbulbFilled />
                                    ) : (
                                        <LightbulbRegular />
                                    )
                                }
                                size="small"
                                appearance="subtle"
                            />
                        </Tooltip>
                    </PopoverTrigger>
                    <PopoverSurface className={styles.popoverContent}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                            <strong>Návazné normy a standardy:</strong>
                            <Button icon={<DismissRegular />} size="small" appearance="subtle" />
                        </div>
                        <ul className={styles.list}>
                            {occurrence.validStandards && occurrence.validStandards.length > 0 ? (
                                occurrence.validStandards.map((standard, idx) => (
                                    <li key={`valid_${idx}`} className={styles.listItem}>
                                        <div className={styles.listItemContent}>
                                            <Tooltip
                                                content={
                                                    standard.replace
                                                        ? "Tato norma bude použita jako náhrada"
                                                        : "Kliknutím vyberte tuto normu jako náhradu"
                                                }
                                                relationship="label"
                                            >
                                                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                    <ArrowSyncRegular
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            event.preventDefault();
                                                            handleReplaceChange({ ...standard, replace: true } as ValidStandard);
                                                        }}
                                                        style={{
                                                            color: standard.replace
                                                                ? tokens.colorBrandForeground1
                                                                : tokens.colorNeutralForeground3,
                                                        }}
                                                    />
                                                    <SearchRegular
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            event.preventDefault();

                                                            const updatedOccurrence = { ...occurrence };
                                                            onReplaceChange(updatedOccurrence, true, standard.identificator);

                                                        }}
                                                        style={{
                                                            color: standard.replace
                                                                ? tokens.colorBrandForeground1
                                                                : tokens.colorNeutralForeground3,
                                                        }}
                                                    />
                                                    {/* <Checkbox
                                                        checked={standard.replace}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onPointerDown={(e) => e.stopPropagation()}

                                                        onChange={(event) => {
                                                            event.stopPropagation();
                                                            event.preventDefault();
                                                            const selectedStandard = occurrence.validStandards?.find((s) => s.replace);
                                                            if (selectedStandard) {
                                                                const updatedOccurrence = { ...occurrence };
                                                                onReplaceChange(updatedOccurrence, true, selectedStandard.identificator);
                                                            }
                                                        }}
                                                        size="medium"
                                                    /> */}
                                                </div>
                                            </Tooltip>
                                            <span style={{ wordBreak: "break-word" }}>
                                                {standard.identificator} {standard.name}{" "}
                                                {standard.validFrom ? `(od ${standard.validFrom})` : ""}
                                            </span>
                                        </div>
                                        <div className={styles.listItemActions}>
                                            {standard.anotation && (
                                                <Popover trapFocus>
                                                    <PopoverTrigger disableButtonEnhancement>
                                                        <Tooltip content="Zobrazit poznámku" relationship="label">
                                                            <Button
                                                                icon={<EditRegular />}
                                                                size="small"
                                                                appearance="subtle"
                                                            />
                                                        </Tooltip>
                                                    </PopoverTrigger>
                                                    <PopoverSurface className={styles.popoverContent}>
                                                        <div>
                                                            <strong>Poznámka:</strong>
                                                            <p>{standard.anotation}</p>
                                                        </div>
                                                    </PopoverSurface>
                                                </Popover>
                                            )}
                                            {standard.description && (
                                                <Popover trapFocus>
                                                    <PopoverTrigger disableButtonEnhancement>
                                                        <Tooltip content="Zobrazit popis" relationship="label">
                                                            <Button
                                                                icon={<InfoRegular />}
                                                                size="small"
                                                                appearance="subtle"
                                                            />
                                                        </Tooltip>
                                                    </PopoverTrigger>
                                                    <PopoverSurface className={styles.popoverContent}>
                                                        <div>
                                                            <strong>Popis:</strong>
                                                            <p>{standard.description ?? "Žádný popis"}</p>
                                                        </div>
                                                    </PopoverSurface>
                                                </Popover>
                                            )}
                                            {standard.detailUrl && (
                                                <Tooltip content="Otevřít náhled/standard" relationship="label">
                                                    <Button
                                                        icon={<EyeRegular />}
                                                        size="small"
                                                        appearance="subtle"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            window.open(standard.detailUrl, "_blank");
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                            <Tooltip content="Kopírovat název" relationship="label">
                                                <Button
                                                    icon={<CopyRegular />}
                                                    size="small"
                                                    appearance="subtle"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(`${standard.identificator} ${standard.name}`);
                                                    }}
                                                />
                                            </Tooltip>
                                        </div>
                                    </li>
                                ))
                            ) : (
                                <li className={styles.listItem}>
                                    <span>Žádné platné normy</span>
                                </li>
                            )}
                        </ul>
                    </PopoverSurface>
                </Popover>
            </div>
        </div>
    );
};

export default InvalidOccurrenceItem;